import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CountrySelect } from '@/components/CountrySelect';
import { ApiError } from '@/lib/api';
import { getGameConfig } from '@swiss-magic/shared-types';
import { ratingFieldLabel, externalIdFieldLabel } from '@/lib/game-labels';

export function AddPlayerForm({
  onAdd,
  onClose,
  gameId,
}: {
  onAdd: (data: {
    firstName: string;
    lastName: string;
    rating?: number;
    fideId?: number;
    city?: string;
    country?: string;
    club?: string;
  }) => Promise<void>;
  onClose: () => void;
  gameId: string;
}) {
  const { t } = useTranslation('director');
  const { t: tc } = useTranslation('common');
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    rating: '',
    fideId: '',
    city: '',
    country: '',
    club: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave() {
    if (!fields.firstName.trim() || !fields.lastName.trim() || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onAdd({
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        ...(fields.rating ? { rating: Number(fields.rating) } : {}),
        ...(fields.fideId ? { fideId: Number(fields.fideId) } : {}),
        ...(fields.city.trim() ? { city: fields.city.trim() } : {}),
        ...(fields.country.trim() ? { country: fields.country.trim() } : {}),
        ...(fields.club.trim() ? { club: fields.club.trim() } : {}),
      });
      onClose();
    } catch (err: unknown) {
      setSaveError(
        err instanceof ApiError
          ? err.userMessage
          : t('players_page.add_form.add_error'),
      );
    } finally {
      setSaving(false);
    }
  }

  const gameConfig = getGameConfig(gameId);
  const formFields = [
    { key: 'firstName', label: t('players_page.add_form.first_name_label'), required: true },
    { key: 'lastName', label: t('players_page.add_form.last_name_label'), required: true },
    ...(gameConfig.playerFields.rating ? [{ key: 'rating', label: ratingFieldLabel(gameConfig, tc), type: 'number' }] : []),
    ...(gameConfig.playerFields.externalId ? [{ key: 'fideId', label: externalIdFieldLabel(gameConfig, tc), type: 'number' }] : []),
    { key: 'city', label: t('players_page.add_form.city_label') },
    { key: 'country', label: t('players_page.add_form.country_label') },
    { key: 'club', label: t('players_page.add_form.club_label') },
  ];

  return (
    <div
      data-testid="add-player-modal"
      className="fixed inset-0 z-50 flex flex-col bg-background"
    >
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-lg font-semibold">{t('players_page.add_form.title')}</h2>
        <Button
          variant="ghost"
          size="sm"
          data-testid="add-player-cancel"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {formFields.map(({ key, label, type, required }) => (
            <div key={key}>
              <label className="mb-1 block text-sm font-medium text-muted-foreground">
                {label}
                {required && <span className="text-red-500"> *</span>}
              </label>
              {key === 'country' ? (
                <CountrySelect
                  data-testid={`add-player-form-${key}`}
                  value={fields.country}
                  onChange={(code) =>
                    setFields((prev) => ({ ...prev, country: code }))
                  }
                />
              ) : (
                <Input
                  data-testid={`add-player-form-${key}`}
                  type={type ?? 'text'}
                  value={fields[key as keyof typeof fields]}
                  onChange={(e) =>
                    setFields((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                />
              )}
            </div>
          ))}
          {saveError && (
            <p className="text-sm text-destructive" data-testid="add-player-error">
              {saveError}
            </p>
          )}
        </div>
      </div>

      <div className="border-t p-4 flex gap-2">
        <Button
          className="flex-1"
          data-testid="add-player-save"
          disabled={saving || !fields.firstName.trim() || !fields.lastName.trim()}
          onClick={handleSave}
        >
          {t('players_page.add_form.save_button')}
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          data-testid="add-player-cancel-bottom"
          onClick={onClose}
        >
          {t('players_page.add_form.cancel_button')}
        </Button>
      </div>
    </div>
  );
}
